import { Flex, Text } from '@mantine/core';
import { NavLink, useLocation } from 'react-router';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { ClientSearch } from './ClientSearch';
import { FONT_WEIGHTS } from '../theme/theme';

type ActiveMatch = 'exact' | 'startsWith' | 'includes';

type RouterTab = {
  label: string;
  href: string;
  activeMatch?: ActiveMatch;
  matchHref?: string;
};

type LocalTab = {
  label: string;
  value: string;
};

type BaseProps = {
  variant?: 'default' | 'bordered';
  showClientSearch?: boolean;
};

type Props = BaseProps &
  (
    | {
        mode: 'router';
        tabs: RouterTab[];
      }
    | {
        mode: 'local';
        tabs: LocalTab[];
        value: string;
        onChange: (value: string) => void;
      }
  );

export default function UnderlineTabs(props: Props) {
  const { showClientSearch, variant = 'default' } = props;
  const cq = useContainerQuery();

  const tabsContent = (
    <Flex gap="md">
      {props.mode === 'router'
        ? props.tabs.map(tab => <RouterTab key={tab.href} {...tab} />)
        : props.tabs.map(tab => (
            <LocalTab
              key={tab.value}
              tab={tab}
              isActive={tab.value === props.value}
              onClick={() => props.onChange(tab.value)}
            />
          ))}
    </Flex>
  );

  return (
    <Flex align="center" gap={cq({ base: 'xl', lg: 'xxl', xl: 'xxxl' })}>
      {showClientSearch && <ClientSearch width={240} />}
      {variant === 'bordered' ? (
        <Flex style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', width: '100%' }}>
          {tabsContent}
        </Flex>
      ) : (
        tabsContent
      )}
    </Flex>
  );
}

function RouterTab(props: RouterTab) {
  const { label, href } = props;
  const style = useIsActiveRouterTab(props)
    ? {
        borderBottom: '2px solid var(--mantine-color-brand-6)',
        marginBottom: -1,
        color: 'black',
        fontWeight: FONT_WEIGHTS.bold,
      }
    : {
        color: 'var(--mantine-color-gray-8)',
        fontWeight: FONT_WEIGHTS.medium,
      };

  return (
    <NavLink to={href} style={{ textDecoration: 'none' }}>
      <Text size="textSm" pb="xxs" px="xxxs" style={style}>
        {label}
      </Text>
    </NavLink>
  );
}

function LocalTab({
  tab,
  isActive,
  onClick,
}: {
  tab: LocalTab;
  isActive: boolean;
  onClick: () => void;
}) {
  const textStyle = isActive
    ? {
        borderBottom: '2px solid var(--mantine-color-brand-6)',
        marginBottom: -1,
        color: 'black',
        fontWeight: FONT_WEIGHTS.medium,
      }
    : {
        color: 'var(--mantine-color-gray-8)',
        fontWeight: FONT_WEIGHTS.medium,
      };

  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      type="button"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      <Text size="textSm" pb="xxs" px="xxxs" style={textStyle}>
        {tab.label}
      </Text>
    </button>
  );
}

function useIsActiveRouterTab(tab: RouterTab) {
  const { pathname } = useLocation();

  const matchHref = tab.matchHref || tab.href;

  if (tab.activeMatch === 'startsWith' || tab.activeMatch === undefined) {
    return pathname.startsWith(matchHref);
  }

  if (tab.activeMatch === 'exact') {
    return pathname === matchHref;
  }

  if (tab.activeMatch === 'includes') {
    return pathname.includes(matchHref);
  }

  return false;
}
